import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Card, CardHeader, Input, Dialog, Bar } from "@ui5/webcomponents-react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import httpRequest from "../../httpRequest";

const CategoriesCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newAccountCode, setNewAccountCode] = useState(""); // New state for account_code
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editAccountCode, setEditAccountCode] = useState(""); // New state for account_code
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const resp = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/categories`);
      setCategories(resp.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      enqueueSnackbar("Failed to fetch categories", { variant: "error" });
    }
  };

  // Add Category
  const handleAddCategory = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/categories`, {
        expense_category: newCategoryName,
        account_code: newAccountCode // Include account_code
      });
      enqueueSnackbar("Category added successfully", { variant: "success" });
      setNewCategoryName("");
      setNewAccountCode(""); // Clear account_code
      setAddDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error("Error adding category:", error);
      enqueueSnackbar("Failed to add category", { variant: "error" });
    }
  };

  // Edit Category
  const handleUpdateCategory = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/categories/${categoryToEdit.id}`, {
        expense_category: editCategoryName,
        account_code: editAccountCode // Include account_code
      });
      enqueueSnackbar("Category updated successfully", { variant: "success" });
      setEditCategoryName("");
      setEditAccountCode(""); // Clear account_code
      setEditDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error("Error updating category:", error);
      enqueueSnackbar("Failed to update category", { variant: "error" });
    }
  };

  // Delete Category
  const handleDeleteCategory = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/categories/${categoryToDelete.id}`);
      enqueueSnackbar("Category deleted successfully", { variant: "success" });
      setDeleteConfirmationOpen(false);
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      enqueueSnackbar("Failed to delete category", { variant: "error" });
    }
  };

  const handleOpenAddDialog = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const handleOpenEditDialog = (category) => {
    setCategoryToEdit(category);
    setEditCategoryName(category.expense_category); // Updated to expense_category
    setEditAccountCode(category.account_code || ""); // Initialize with current account_code
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => setEditDialogOpen(false);

  const handleOpenDeleteDialog = (category) => {
    setCategoryToDelete(category);
    setDeleteConfirmationOpen(true);
  };

  const cancelDeleteCategory = () => setDeleteConfirmationOpen(false);

  return (
    <>
      <Card header={<CardHeader subtitleText="Create and manage categories" titleText="Expense Categories" />}>
        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <input
            type="text"
            placeholder="Search Categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: "1", padding: "10px", marginRight: "20px" }}
          />
          <ButtonContained variant="contained" color="primary" onClick={handleOpenAddDialog}>
            <AddIcon /> Add New
          </ButtonContained>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto', minWidth: 600 }}>Category Name</TableCell>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto' }}>Account Code</TableCell> {/* New column for account_code */}
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories
              .filter(category => category.expense_category.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((category, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{category.expense_category}</TableCell>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{category.account_code}</TableCell> {/* Display account_code */}
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>
                    <ButtonContained style={{ marginRight: 10 }} onClick={() => handleOpenEditDialog(category)}>
                      <EditIcon /> Edit
                    </ButtonContained>
                    <ButtonOutlined onClick={() => handleOpenDeleteDialog(category)}>
                      <DeleteIcon /> Delete
                    </ButtonOutlined>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {/* Delete Confirmation Dialog */}
        <Dialog
          headerText="Confirm Delete"
          open={deleteConfirmationOpen}
          onAfterClose={cancelDeleteCategory}
          state="Critical"
          footer={
            <Bar design="Footer" endContent={
              <>
                <ButtonOutlined style={{ marginRight: 8 }} onClick={cancelDeleteCategory}>
                  Cancel
                </ButtonOutlined>
                <ButtonContained onClick={handleDeleteCategory}>
                  Delete
                </ButtonContained>
              </>
            } />
          }
        >
          Are you sure you want to delete this category?
        </Dialog>

        {/* Edit Category Dialog */}
        <Dialog
          headerText={`Edit Category`}
          open={editDialogOpen}
          onAfterClose={handleCloseEditDialog}
          footer={
            <Bar design="Footer" endContent={
              <>
                <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseEditDialog}>
                  Cancel
                </ButtonOutlined>
                <ButtonContained onClick={handleUpdateCategory}>
                  Update
                </ButtonContained>
              </>
            } />
          }
        >
          <Input
            placeholder="Category name"
            value={editCategoryName}
            onChange={(e) => setEditCategoryName(e.target.value)}
            showSuggestions={false}
          />
		  <br />
          <Input
            placeholder="Account code"
            value={editAccountCode}
            onChange={(e) => setEditAccountCode(e.target.value)}
            showSuggestions={false}
            style={{ marginTop: "10px" }} // Added spacing
          />
        </Dialog>

        {/* Add Category Dialog */}
        <Dialog
          headerText={`Add Category`}
          open={addDialogOpen}
          onAfterClose={handleCloseAddDialog}
          footer={
            <Bar design="Footer" endContent={
              <>
                <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseAddDialog}>
                  Cancel
                </ButtonOutlined>
                <ButtonContained onClick={handleAddCategory}>
                  Add
                </ButtonContained>
              </>
            } />
          }
        >
          <Input
            placeholder="New Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            showSuggestions={false}
          />
		  <br />
          <Input
            placeholder="Account code"
            value={newAccountCode}
            onChange={(e) => setNewAccountCode(e.target.value)}
            showSuggestions={false}
            style={{ marginTop: "10px" }} // Added spacing
          />
        </Dialog>
      </Card>
    </>
  );
};

export default CategoriesCard;
